import { create } from 'zustand';
import { api } from '@/shared/services/api';
import { announceSession, listenSession, identityMismatch, type SessionIdentity } from '../lib/session-channel';
import type { Permission } from '@softfactura/shared';
import { useFormatPrefsStore } from '@/shared/stores/format-prefs-store';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: string;
  /**
   * Effective permission set for the current user, computed server-side
   * (built-in role + tenant overrides + custom-role definitions). This is
   * the single source of truth — frontend gates should consume it directly
   * rather than re-resolving from `role`.
   *
   * Optional because some auth responses set `user` from a partial payload
   * that doesn't include the field; the subsequent `refreshProfile()` call
   * repopulates it. Hooks read with `permissions ?? []` and fail closed.
   */
  permissions?: readonly Permission[];
  emailVerified: boolean;
  /** Sesiune de impersonare activă (admin conectat în contul unui utilizator) —
   *  derivat server-side din cookie-ul semnat sf_impersonation. Gate-urile de
   *  verify-email NU se aplică adminului aflat în impersonare (incident 2026-07-15). */
  impersonating?: boolean;
  twofaEnabled?: boolean;
  briefingEnabled?: boolean;
  defaultStockDecrementOnIssue?: boolean;
  isSuperAdmin?: boolean;
  uiTourCompletedAt?: string | null;
  uiTourSkippedAt?: string | null;
  uiCompletedTours?: string[];
  uiSkippedTours?: string[];
}

interface AuthCompany {
  id: string;
  name: string;
  cui: string | null;
  onboardingCompleted: boolean;
}

/** Membership entry returned by /auth/profile pentru switcher-ul multi-firma. */
export interface AuthMembership {
  id: string;
  name: string;
  cui: string | null;
  role: string;
  isDefault: boolean;
  /** True pentru firma principala (cea cu care s-a inregistrat contul — membership-ul cel mai vechi). */
  isPrimary?: boolean;
  onboardingCompleted: boolean;
  deletionRequestedAt?: string | null;
  deletionScheduledFor?: string | null;
  canLeave?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  /** Compania (firma) curentă din JWT/sesiune. */
  company: AuthCompany | null;
  /** @deprecated Use `company`. */
  tenant: AuthCompany | null;
  /** Lista firmelor la care userul are membership (pentru switcher topbar). */
  tenants: AuthMembership[];
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string, twoFactorCode?: string, trustDevice?: 'month' | 'permanent') => Promise<{ requiresTwoFactor?: boolean }>;
  register: (data: { name: string; email: string; password: string; acceptTerms: boolean; acceptPrivacy: boolean; acceptUnusualTerms: boolean; acceptMarketing?: boolean; trafficSource?: any; referralCode?: string; honeypot?: string }) => Promise<void>;
  logout: () => Promise<void>;
  setAuth: (user: AuthUser, company: AuthCompany) => void;
  updatePreferences: (prefs: { briefingEnabled?: boolean; defaultStockDecrementOnIssue?: boolean }) => Promise<void>;
  initialize: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** Comuta la alta firma. Re-emite cookie + face hard reload pentru cache curat.
   *  redirectTo (default '/') permite redirectare custom dupa switch (ex. '/onboarding'). */
  switchTenant: (tenantId: string, redirectTo?: string) => Promise<void>;
  /** Marcheaza o firma ca default (auto-selectata la login). */
  setDefaultTenant: (tenantId: string) => Promise<void>;
}

type ProfileResponse = {
  id: string;
  name: string;
  email: string;
  role: string;
  permissions?: readonly Permission[];
  emailVerified: boolean;
  impersonating?: boolean;
  twofaEnabled?: boolean;
  briefingEnabled?: boolean;
  defaultStockDecrementOnIssue?: boolean;
  isSuperAdmin?: boolean;
  tenant: AuthCompany;
  tenants?: AuthMembership[];
  uiTourCompletedAt?: string | null;
  uiTourSkippedAt?: string | null;
  uiCompletedTours?: string[];
  uiSkippedTours?: string[];
  /** Preferințele de aspect salvate în cont (aceleași ca GET /auth/appearance-prefs). */
  appearancePrefs?: AccountAppearancePrefs;
};

/** Preferințele de aspect din cont, cum vin în /auth/profile și în răspunsul de login. */
type AccountAppearancePrefs = { formatPrefs?: { numberFormat?: unknown } | null } | null;

/**
 * Formatul numeric salvat în cont, aplicat înainte ca dashboard-ul să apară: altfel
 * AppearancePrefsSync îl aplica după montare, iar NumberFormatProvider (key={numberFormat})
 * reconstruia tot dashboard-ul (browser nou, fereastră privată, schimbare pe alt dispozitiv).
 */
function applyAccountNumberFormat(appearancePrefs: AccountAppearancePrefs | undefined): void {
  const saved = appearancePrefs?.formatPrefs?.numberFormat;
  if (saved !== 'en-US' && saved !== 'ro-RO') return;
  if (useFormatPrefsStore.getState().numberFormat === saved) return;
  try {
    useFormatPrefsStore.setState({ numberFormat: saved });
  } catch (err) {
    // persist aplică valoarea în memorie ÎNAINTE să scrie în localStorage; o scriere eșuată
    // (spațiu plin, stocare dezactivată) nu transformă o sesiune validă în deconectare.
    console.warn('[auth] formatul numeric din cont e aplicat, dar nu s-a putut salva local:', err);
  }
}

function buildUserState(profile: ProfileResponse): AuthUser {
  // Detect a broken-role state: any non-empty role that resolved to zero
  // permissions points to either a deleted custom role or a server-side
  // misconfig. Surface in the console so support can spot it; UI gates
  // already fail closed (user sees an empty dashboard rather than a crash).
  if (profile.role && profile.role !== 'api_user' && (!profile.permissions || profile.permissions.length === 0)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[auth] User has role "${profile.role}" but no resolved permissions. ` +
      'The role may have been deleted or misconfigured. Contact your administrator.',
    );
  }
  return {
    id: profile.id,
    name: profile.name,
    email: profile.email,
    role: profile.role,
    permissions: profile.permissions ?? [],
    briefingEnabled: profile.briefingEnabled,
    defaultStockDecrementOnIssue: profile.defaultStockDecrementOnIssue,
    isSuperAdmin: profile.isSuperAdmin,
    emailVerified: profile.emailVerified,
    impersonating: profile.impersonating === true,
    twofaEnabled: profile.twofaEnabled || false,
    uiTourCompletedAt: profile.uiTourCompletedAt ?? null,
    uiTourSkippedAt: profile.uiTourSkippedAt ?? null,
    uiCompletedTours: profile.uiCompletedTours ?? [],
    uiSkippedTours: profile.uiSkippedTours ?? [],
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  company: null,
  tenant: null,
  tenants: [],
  isAuthenticated: false,
  isLoading: true,

  initialize: async () => {
    // Prevent double initialization (React StrictMode / multiple mounts)
    const state = useAuthStore.getState();
    if (!state.isLoading) return;
    installSessionGuards();
    try {
      const profile = await api.get<ProfileResponse>('/auth/profile');
      applyAccountNumberFormat(profile.appearancePrefs); // cât încă se vede spinnerul (isLoading: true)
      set({
        user: buildUserState(profile),
        company: profile.tenant,
        tenant: profile.tenant,
        tenants: profile.tenants ?? [],
        isAuthenticated: true,
        isLoading: false,
      });
      // Serverul poate schimba identitatea fără ca taburile vechi să afle (invitație acceptată,
      // link din e-mail, impersonare pornită): le-o anunțăm, ca să nu lucreze sub interfața veche
      // cu cookie-ul nou. Doar la succes — un tab fără sesiune nu-i deconectează pe ceilalți.
      announceSession(currentIdentity());
    } catch {
      set({ isLoading: false, isAuthenticated: false });
    }
  },

  refreshProfile: async () => {
    try {
      const before = currentIdentity();
      const profile = await api.get<ProfileResponse>('/auth/profile');
      const incoming = { userId: profile.id ?? null, tenantId: profile.tenant?.id ?? null };
      if (before.userId && identityMismatch(before, incoming)) {
        // Tabul avea altă identitate: nu o adoptăm pe loc (antet nou peste cache-ul și formularele
        // vechi) — o anunțăm și reîncărcăm pe sesiunea nouă.
        announceSession(incoming);
        reloadForSessionChangedElsewhere();
        return;
      }
      set({
        user: buildUserState(profile),
        company: profile.tenant,
        tenant: profile.tenant,
        tenants: profile.tenants ?? [],
        isAuthenticated: true,
      });
      // Identitate nouă de la server (ex. verificarea e-mailului a deschis alt cont): o anunțăm.
      const after = currentIdentity();
      if (after.userId !== before.userId || after.tenantId !== before.tenantId) announceSession(after);
    } catch {
      // Silently fail on refresh
    }
  },

  login: async (email, password, twoFactorCode, trustDevice) => {
    const response = await api.post<{
      requiresTwoFactor?: boolean;
      user?: AuthUser & { appearancePrefs?: AccountAppearancePrefs };
      tenant?: AuthCompany;
      tenants?: AuthMembership[];
    }>('/auth/login', { email, password, twoFactorCode, trustDevice });

    if (response.requiresTwoFactor) {
      return { requiresTwoFactor: true };
    }

    if (response.user) {
      const { appearancePrefs, ...user } = response.user;
      applyAccountNumberFormat(appearancePrefs); // înainte să apară dashboard-ul (browser nou)
      set({
        user,
        company: response.tenant ?? null,
        tenant: response.tenant ?? null,
        tenants: response.tenants ?? [],
        isAuthenticated: true,
      });
      // Hardening multi-tab: taburile vechi (posibil pe ALT cont) afla acum
      // si se reincarca pe sesiunea noua, in loc sa o rescrie prin refresh.
      announceSession({ userId: response.user.id, tenantId: response.tenant?.id ?? null });
    }

    return {};
  },

  register: async (data) => {
    const response = await api.post<{
      user: AuthUser;
      tenant: AuthCompany;
      tenants?: AuthMembership[];
    }>('/auth/register', data);

    set({
      user: response.user,
      company: response.tenant,
      tenant: response.tenant,
      tenants: response.tenants ?? [],
      isAuthenticated: true,
    });
    announceSession({ userId: response.user.id, tenantId: response.tenant?.id ?? null });
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors on logout
    }

    // isLoading: true — până la reîncărcare, AuthGuard arată „se încarcă” în loc să trimită singur
    // la /login în aplicație (acolo ar rămâne în memorie datele utilizatorului deconectat).
    set({
      user: null,
      company: null,
      tenant: null,
      tenants: [],
      isAuthenticated: false,
      isLoading: true,
    });
    announceSession({ userId: null, tenantId: null });

    // Curatam preferintele de aspect din localStorage la logout. Daca alt
    // user se autentifica pe acelasi browser, bootstrap script-ul nu va mai
    // citi paleta/fontul fostului user — defaults brut → fetch din DB →
    // preferintele noului user. Tranzitie curata, fara flash-uri duble.
    //
    // De asemenea, curatam draft-urile locale (`invoice-draft-backup:*` v1+v2,
    // `nir.*`). Fara asta, un user pe browser shared (sediu, internet cafe)
    // ar lasa drafturi cu date confidentiale (clienti, sume) in localStorage,
    // vizibile la urmatoarea autentificare.
    try {
      localStorage.removeItem('sf-theme-prefs');
      localStorage.removeItem('sf-font-prefs');
      localStorage.removeItem('sf-format-prefs');
      localStorage.removeItem('sf-sidebar-open-group');
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (k.startsWith('invoice-draft-backup:') || k.startsWith('nir.')) {
          toRemove.push(k);
        }
      }
      toRemove.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
    } catch { /* sandbox/quota — ignore */ }
    // „Acum vezi: {firma}” rămas de la o schimbare de firmă nu mai are ce căuta pe /login.
    try { sessionStorage.removeItem('tenantSwitchedTo'); } catch { /* ignore */ }

    // Reîncărcare completă, nu navigare în aplicație: altfel în același tab rămân în memorie
    // datele încărcate ale utilizatorului anterior (React Query: 5 minute „proaspete”, chei fără
    // firmă/utilizator), plus formatul, tema și fontul lui — următorul utilizator le-ar vedea, iar
    // dacă n-are preferințe în cont, i s-ar scrie cele moștenite.
    await leaveToFreshPage(() => window.location.replace('/login'));
  },

  updatePreferences: async (prefs) => {
    const current = useAuthStore.getState().user;
    if (!current) return;
    // Optimistic update
    set({ user: { ...current, ...prefs } });
    try {
      await api.patch('/auth/preferences', prefs);
    } catch (err) {
      // Revert on failure
      set({ user: current });
      throw err;
    }
  },

  setAuth: (user, tenant) => {
    set({ user, company: tenant, tenant, isAuthenticated: true, isLoading: false });
  },

  switchTenant: async (tenantId: string, redirectTo: string = '/') => {
    // Backend re-emite cookie cu currentTenantId nou. Hard reload =>
    // React Query cache + tot state-ul React se reseteaza. Pattern identic
    // cu ImpersonationBanner: schimbare context activa.
    // redirectTo permite redirectare custom (ex. '/onboarding' pentru firma
    // noua care nu are setarile completate inca).
    await api.post('/auth/switch-tenant', { tenantId });
    // Celelalte taburi raman altfel pe firma veche cu cookie-ul firmei noi.
    const me = useAuthStore.getState().user;
    announceSession({ userId: me?.id ?? null, tenantId });
    if (typeof window !== 'undefined') {
      // Salveaza numele firmei in sessionStorage ca toast-ul post-reload sa
      // poata afisa "Acum vezi {firma}". sessionStorage supravietuieste
      // hard reload-ului dar e pe-tab, deci nu polueaza alte tab-uri.
      const target = useAuthStore.getState().tenants.find((t) => t.id === tenantId);
      if (target) {
        try {
          sessionStorage.setItem('tenantSwitchedTo', target.name);
        } catch {
          // sessionStorage poate fi blocat (private browsing) — ignoram, e doar UI
        }
      }
    }
    await leaveToFreshPage(() => {
      window.location.href = redirectTo;
    });
  },

  setDefaultTenant: async (tenantId: string) => {
    await api.post('/tenants/set-default', { tenantId });
    // Update local state ca sa se vada efectul imediat in UI
    set((state) => ({
      tenants: state.tenants.map((t) => ({ ...t, isDefault: t.id === tenantId })),
    }));
  },

}));

/**
 * Pleacă spre o pagină încărcată de la zero abia după ce React a randat „se încarcă” (isLoading:
 * AuthGuard nu mai ține montate paginile din aplicație). Formularele nesalvate se demontează și își
 * scot garda beforeunload, deci browserul nu mai întreabă „Părăsești pagina?”. Altfel, la „Rămâi”,
 * pagina rămânea cu datele și formularele identității vechi, în timp ce cererile plecau deja cu
 * sesiunea nouă: o salvare ajungea în altă firmă sau în alt cont (verificat în Chromium, 30.09.2026).
 * Pe paginile din afara AuthGuard (login, pagini publice) nu e nimic de demontat și nici gardă.
 */
export async function leaveToFreshPage(go: () => void): Promise<void> {
  // Din acest moment nicio cerere nu mai pleacă din pagina asta (sesiunea ei s-a încheiat).
  api.sessionChanged = true;
  useAuthStore.setState({ isLoading: true });
  await new Promise((resolve) => setTimeout(resolve, 0));
  if (typeof window !== 'undefined') go();
}

/**
 * Sesiunea s-a schimbat pe sub pagină (alt tab, un refresh sau profilul întors de server au altă
 * identitate): reîncărcăm, iar după reîncărcare un mesaj spune de ce (vezi providers.tsx).
 */
function reloadForSessionChangedElsewhere(): void {
  try {
    // Valoare unică: închiderea unui mesaj mai vechi n-o șterge pe asta (vezi showAfterReload).
    sessionStorage.setItem('sessionChangedElsewhere', String(Date.now()));
    // „Acum vezi: {firma}” rămas de la o schimbare de firmă ar apărea cu un nume greșit.
    sessionStorage.removeItem('tenantSwitchedTo');
  } catch {
    // sessionStorage blocat — doar mesajul lipsește
  }
  void leaveToFreshPage(() => window.location.reload());
}

// ── Hardening multi-tab (2026-06-11) ─────────────────────────────────────────
// O singură instalare per pagină: (1) listener cross-tab — alt tab a schimbat
// sesiunea → dacă identitatea diferă de a noastră, reload (preluăm sesiunea
// din cookie); (2) callback pe clientul API — după orice refresh de token,
// identitatea din răspuns trebuie să corespundă celei afișate, altfel reload.
// Reload-ul re-rulează initialize() → profil = cookie → stare consistentă;
// fără buclă (identitățile coincid după reload).
let sessionGuardsInstalled = false;

function currentIdentity(): SessionIdentity {
  const st = useAuthStore.getState();
  return { userId: st.user?.id ?? null, tenantId: st.tenant?.id ?? null };
}

function installSessionGuards(): void {
  if (sessionGuardsInstalled || typeof window === 'undefined') return;
  sessionGuardsInstalled = true;

  listenSession((incoming) => {
    const st = useAuthStore.getState();
    // În timpul bootstrap-ului nu avem încă identitate de comparat.
    if (st.isLoading) return;
    if (identityMismatch(currentIdentity(), incoming)) reloadForSessionChangedElsewhere();
  });

  api.onRefreshIdentity = (incoming) => {
    const st = useAuthStore.getState();
    if (!st.user) return false; // bootstrap/deconectare — profilul va fi oricum al cookie-ului
    if (!identityMismatch(currentIdentity(), incoming)) return false;
    // Alt cont/altă firmă în cookie: cererea care a cerut refresh-ul nu se reia (ar ajunge în altă
    // firmă). Dacă nu plecăm deja (ex. schimbarea firmei în curs), reîncărcăm pe sesiunea nouă.
    if (!st.isLoading) reloadForSessionChangedElsewhere();
    return true;
  };
}
